import { useEffect, useId, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { errorText } from "./api";
import { formatDateTime } from "./format";
import type { EffectiveScorecard, Scorecard, ScorecardCriterion } from "./types";

const SOURCE_TEXT: Record<string, string> = {
  default: "No scorecard is set, so the built-in default is used.",
  organization: "Your organization's scorecard.",
  agent: "This agent's own scorecard.",
};

const lines = (text: string) => text.split("\n").map((l) => l.trim()).filter(Boolean);

interface Draft {
  criteria: Array<Omit<ScorecardCriterion, "weight"> & { weight: string }>;
  pass_threshold: string;
  competitors: string;
  prohibited_claims: string;
  required_disclosures: string;
}

const toDraft = (s: Scorecard): Draft => ({
  criteria: s.criteria.map((c) => ({ ...c, weight: String(c.weight) })),
  pass_threshold: String(s.pass_threshold),
  competitors: s.competitors.join("\n"),
  prohibited_claims: s.prohibited_claims.join("\n"),
  required_disclosures: s.required_disclosures.join("\n"),
});

export function scorecardFromDraft(d: Draft): { scorecard: Scorecard; problems: string[] } {
  const problems: string[] = [];
  const criteria = d.criteria.map((c, i) => {
    const weight = Number(c.weight);
    if (!c.id.trim() || !c.label.trim()) problems.push(`Criterion ${i + 1} needs an id and a label.`);
    if (!c.weight.trim() || !Number.isFinite(weight) || weight <= 0 || weight > 100) {
      problems.push(`Criterion ${i + 1} needs a weight above 0 and at most 100.`);
    }
    return { id: c.id.trim(), label: c.label.trim(), description: c.description.trim(), weight };
  });
  if (!criteria.length) problems.push("Add at least one criterion.");
  const ids = criteria.map((c) => c.id).filter(Boolean);
  if (new Set(ids).size !== ids.length) problems.push("Criterion ids must be unique.");
  const threshold = Number(d.pass_threshold);
  if (!d.pass_threshold.trim() || !Number.isFinite(threshold) || threshold < 0 || threshold > 100) {
    problems.push("Pass mark must be between 0 and 100.");
  }
  return {
    scorecard: {
      criteria,
      pass_threshold: threshold,
      competitors: lines(d.competitors),
      prohibited_claims: lines(d.prohibited_claims),
      required_disclosures: lines(d.required_disclosures),
    },
    problems,
  };
}

function ListField({ label, value, onChange, disabled, hint }: {
  label: string; value: string; onChange: (v: string) => void; disabled: boolean; hint: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-ink-2">{label}</label>
      <textarea
        id={id}
        rows={3}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-line bg-elevated px-2 py-1.5 text-sm text-ink disabled:opacity-60"
      />
      <span className="text-[11px] text-ink-3">{hint}</span>
    </div>
  );
}

/** Saved whole: criteria weights and the pass mark only make sense together. */
export function ScorecardEditor({
  effective,
  readOnly,
  onSave,
  saveLabel = "Save scorecard",
}: {
  effective: EffectiveScorecard;
  readOnly: boolean;
  onSave: (scorecard: Scorecard) => Promise<unknown>;
  saveLabel?: string;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(effective));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(toDraft(effective)), [effective]);
  const thresholdId = useId();

  const { scorecard, problems } = scorecardFromDraft(draft);
  const dirty = JSON.stringify(toDraft(effective)) !== JSON.stringify(draft);
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  const setCriterion = (index: number, patch: Partial<Draft["criteria"][number]>) =>
    set({ criteria: draft.criteria.map((c, i) => (i === index ? { ...c, ...patch } : c)) });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(scorecard);
    } catch (e) {
      setError(errorText(e, "The scorecard could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-3">
        {SOURCE_TEXT[effective.source] ?? `From ${effective.source}.`}
        {effective.updated_at ? ` Last updated ${formatDateTime(effective.updated_at)}.` : ""}
      </p>

      <fieldset className="space-y-2" disabled={readOnly}>
        <legend className="text-xs font-semibold uppercase tracking-wide text-ink-3">Criteria</legend>
        {draft.criteria.length === 0 ? <p className="text-sm text-ink-3">No criteria.</p> : null}
        {draft.criteria.map((c, i) => (
          <div key={i} className="grid gap-2 rounded-xl border border-line p-2 sm:grid-cols-[8rem_1fr_6rem_auto]">
            <input aria-label={`Criterion ${i + 1} id`} placeholder="id" value={c.id} onChange={(e) => setCriterion(i, { id: e.target.value })} className="h-9 rounded-lg border border-line bg-elevated px-2 font-mono text-xs text-ink" />
            <input aria-label={`Criterion ${i + 1} label`} placeholder="Label" value={c.label} onChange={(e) => setCriterion(i, { label: e.target.value })} className="h-9 rounded-lg border border-line bg-elevated px-2 text-sm text-ink" />
            <input aria-label={`Criterion ${i + 1} weight`} type="number" min={0} placeholder="Weight" value={c.weight} onChange={(e) => setCriterion(i, { weight: e.target.value })} className="h-9 rounded-lg border border-line bg-elevated px-2 text-sm text-ink" />
            {!readOnly ? (
              <button type="button" aria-label={`Remove criterion ${i + 1}`} onClick={() => set({ criteria: draft.criteria.filter((_, j) => j !== i) })} className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-3 hover:bg-soft hover:text-danger">
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            ) : <span />}
            <textarea aria-label={`Criterion ${i + 1} description`} placeholder="What a pass looks like" rows={2} value={c.description} onChange={(e) => setCriterion(i, { description: e.target.value })} className="rounded-lg border border-line bg-elevated px-2 py-1.5 text-sm text-ink sm:col-span-4" />
          </div>
        ))}
        {!readOnly ? (
          <button type="button" onClick={() => set({ criteria: [...draft.criteria, { id: "", label: "", description: "", weight: "10" }] })} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand">
            <Plus className="h-4 w-4" aria-hidden /> Add criterion
          </button>
        ) : null}
      </fieldset>

      <div className="flex flex-col gap-1">
        <label htmlFor={thresholdId} className="text-xs font-medium text-ink-2">Pass mark (0–100)</label>
        <input id={thresholdId} type="number" min={0} max={100} disabled={readOnly} value={draft.pass_threshold} onChange={(e) => set({ pass_threshold: e.target.value })} className="h-9 w-28 rounded-lg border border-line bg-elevated px-2 text-sm text-ink disabled:opacity-60" />
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <ListField label="Competitors" hint="One per line." value={draft.competitors} disabled={readOnly} onChange={(v) => set({ competitors: v })} />
        <ListField label="Prohibited claims" hint="One per line." value={draft.prohibited_claims} disabled={readOnly} onChange={(v) => set({ prohibited_claims: v })} />
        <ListField label="Required disclosures" hint="One per line." value={draft.required_disclosures} disabled={readOnly} onChange={(v) => set({ required_disclosures: v })} />
      </div>

      {!readOnly ? (
        <div className="space-y-2">
          {dirty && problems.length ? (
            <ul role="alert" className="list-disc pl-5 text-sm text-danger">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty || problems.length > 0}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {saveLabel}
            </button>
            {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
